import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Product } from '@/lib/api';

export type CartItem = {
  product: Product;
  quantity: number;
  selectedSize?: string;
};

type CartContextType = {
  items: CartItem[];
  addItem: (product: Product, quantity: number, selectedSize?: string) => void;
  removeItem: (slug: string, selectedSize?: string) => void;
  updateQuantity: (slug: string, quantity: number, selectedSize?: string) => void;
  clearCart: () => void;
  totalItems: number;
  subtotal: number;
};

const CartContext = createContext<CartContextType | null>(null);

function sameLine(item: CartItem, slug: string, selectedSize?: string) {
  return item.product.slug === slug && item.selectedSize === selectedSize;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);

  const addItem = useCallback((product: Product, quantity: number, selectedSize?: string) => {
    setItems(prev => {
      const existing = prev.find(i => sameLine(i, product.slug, selectedSize));
      if (existing) {
        return prev.map(i =>
          sameLine(i, product.slug, selectedSize)
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, { product, quantity, selectedSize }];
    });
  }, []);

  const removeItem = useCallback((slug: string, selectedSize?: string) => {
    setItems(prev => prev.filter(i => !sameLine(i, slug, selectedSize)));
  }, []);

  const updateQuantity = useCallback((slug: string, quantity: number, selectedSize?: string) => {
    if (quantity <= 0) {
      removeItem(slug, selectedSize);
      return;
    }
    setItems(prev =>
      prev.map(i => (sameLine(i, slug, selectedSize) ? { ...i, quantity } : i))
    );
  }, [removeItem]);

  const clearCart = useCallback(() => setItems([]), []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, totalItems, subtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
